import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Spinner,
  Button,
  Alert,
  Card,
} from "react-bootstrap";
import NavBar from "../../components/Heritage/NavigationBar";
import DOMPurify from "dompurify";

const BeautyDetails = () => {
  const { name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const beautyFromState = location.state?.beauty;

  const [beautyDetails, setBeautyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sanitizedSteps, setSanitizedSteps] = useState("");

  // inline styles isolated for this page
  const styles = {
    card: {
      border: "none",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      borderRadius: "12px",
    },
    sectionTitle: {
      fontWeight: 600,
      marginBottom: "10px",
    },
    img: {
      width: "100%",
      maxHeight: "400px",
      objectFit: "cover",
      borderRadius: "12px",
    },
    stepsBox: {
      maxHeight: "380px",
      overflowY: "auto",
      paddingRight: "10px",
    },
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await fetch(
          "https://www.bharatabroad.com/api/ai/beauty-details",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name }),
          }
        );

        if (!res.ok) throw new Error("Failed to fetch AI details");

        const data = await res.json();
        setBeautyDetails(data);

        const safeSteps = DOMPurify.sanitize(data.stepsHtml || "", {
          ALLOWED_TAGS: ["p", "ul", "li", "b", "i", "h3", "h4", "img"],
          ALLOWED_ATTR: ["src", "alt", "style"],
        });

        setSanitizedSteps(safeSteps);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [name]);

  const beautyImage = beautyFromState?.image || beautyDetails?.image;
  const beautyDescription =
    beautyFromState?.description || beautyDetails?.description;

  // LOADING UI
  if (loading)
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
        <p className="mt-2">
          Generating details for <b>{name}</b>...
        </p>
      </div>
    );

  // ERROR UI
  if (error)
    return (
      <Container className="py-5 text-center">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );

  if (!beautyDetails)
    return (
      <Container className="py-5 text-center">
        <p className="text-danger">Error: No details found.</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </Container>
    );

  return (
    <>
      <NavBar />

      <Container fluid className="mt-4">
        <Row>
          {/* LEFT CONTENT */}
          <Col lg={9}>
            <h2 className="fw-bold">{name}</h2>
            {beautyDescription && (
              <p className="text-muted">{beautyDescription}</p>
            )}
            <hr />

            {/* HISTORY */}
            {beautyDetails.history && (
              <Card style={styles.card} className="mb-4">
                <Card.Body>
                  <h5 style={styles.sectionTitle}>History</h5>
                  <p className="text-secondary">{beautyDetails.history}</p>
                </Card.Body>
              </Card>
            )}

            {/* IMAGE + STEPS */}
            {(beautyImage ||
              beautyDetails.steps?.length > 0 ||
              sanitizedSteps) && (
              <Row className="mb-4">
                {/* IMAGE */}
                <Col md={6} className="mb-3">
                  {beautyImage && (
                    <Card style={styles.card}>
                      <Card.Img
                        src={beautyImage}
                        alt={name}
                        style={styles.img}
                      />
                    </Card>
                  )}
                </Col>

                {/* STEPS */}
                <Col md={6}>
                  <Card style={styles.card} className="h-100">
                    <Card.Body>
                      <h5 style={styles.sectionTitle}>Steps / Procedure</h5>

                      {sanitizedSteps ? (
                        <div
                          style={styles.stepsBox}
                          className="text-secondary"
                          dangerouslySetInnerHTML={{ __html: sanitizedSteps }}
                        />
                      ) : (
                        <ul className="text-secondary">
                          {beautyDetails.steps.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ul>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* BENEFITS */}
            {beautyDetails.benefits && (
              <Card style={styles.card} className="mb-4">
                <Card.Body>
                  <h5 style={styles.sectionTitle}>Benefits</h5>
                  <p className="text-secondary">{beautyDetails.benefits}</p>
                </Card.Body>
              </Card>
            )}

            {/* TIPS */}
            {beautyDetails.tips && (
              <Card style={styles.card} className="mb-4">
                <Card.Body>
                  <h5 style={styles.sectionTitle}>Tips / Precautions</h5>
                  <p className="text-secondary">{beautyDetails.tips}</p>
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* RIGHT ADS */}
          <Col lg={3}>
            <h6
              className="text-uppercase text-secondary mb-2"
              style={{ fontSize: "11px" }}
            >
              Advertisement
            </h6>

            <Card style={styles.card} className="mb-3">
              <Card.Img
                src="https://s0.2mdn.net/simgad/6463644956967963792"
                alt="Ad 1"
                height="250"
                style={{ objectFit: "cover" }}
              />
            </Card>

            <Card style={styles.card}>
              <Card.Img
                src="https://s0.2mdn.net/simgad/6463644956967963792"
                alt="Ad 2"
                height="250"
                style={{ objectFit: "cover" }}
              />
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default BeautyDetails;
